import { Suspense } from 'react';
import { createApiRef, useApiHolder } from '@backstage/frontend-plugin-api';
import {
  TechDocsAddonOptions,
  TechDocsAddons,
} from '@backstage/plugin-techdocs-react';
import { attachTechDocsAddonComponentData } from '@backstage/plugin-techdocs-react/alpha';

/**
 * The TechDocs addons API (`api:techdocs/addons` of
 * `@backstage/plugin-techdocs/alpha`), which collects every `AddonBlueprint`
 * extension in the app. The core plugin keeps its API ref internal, so this
 * ref mirrors its id; `techdocsAddons.test.tsx` checks that they still match.
 */
export const techdocsAddonsApiRef = createApiRef<{
  getAddons(): TechDocsAddonOptions[];
}>({
  id: 'plugin.techdocs.addons',
});

/**
 * The addons registered with the TechDocs addons API. Empty when the TechDocs
 * plugin is not installed, for example in a test app.
 */
export const useTechDocsAddonOptions = (): TechDocsAddonOptions[] => {
  const apiHolder = useApiHolder();
  return apiHolder.get(techdocsAddonsApiRef)?.getAddons() ?? [];
};

/**
 * The `<TechDocsAddons>` registry element for the given addons, built the way
 * the core reader page builds it. Use it as the outlet element of the route
 * that renders the TechDocs reader page: `TechDocsReaderPageContent` finds the
 * addons through `useTechDocsAddons()`, which walks the outlet's element tree
 * without rendering it, so the outlet must be this element itself, not a
 * component that renders it.
 */
export function renderTechDocsAddons(
  addons: TechDocsAddonOptions[],
): JSX.Element {
  return (
    <TechDocsAddons>
      {addons.map(options => {
        const Addon = options.component;
        attachTechDocsAddonComponentData(Addon, options);
        return (
          <Suspense key={options.name} fallback={null}>
            <Addon />
          </Suspense>
        );
      })}
    </TechDocsAddons>
  );
}
